import { computed, ComputedRef, Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import unionBy from 'lodash/unionBy'
import { DocumentParameter } from '@vue/apollo-composable/dist/useQuery'
import { IsoCode } from '@/types/isoCode'
import {
  AccountType,
  ContactStatus,
  Hire,
  TrustedContactManagedStatus,
  TrustedContactOrigin,
  TrustedContactStaffingAgencyStatus,
} from '@/gql/graphql'
import useDate from '@/composables/useDate'

export type TrustedContactListVariables = {
  accounts: string[]
  allAccounts: string[]
  statuses: string[]
  origin: TrustedContactOrigin | null
  page: number
  perPage: number
  search: string | null
  orderBy: Object[]
  markets: string[]
  invitedByUsers: string[]
  skills: string[]
  companyId: string
  hireHistory: Object[]
  hireStatus: Object[]
  staffingAgencyStatus: TrustedContactStaffingAgencyStatus | null
}
export type TrustedContactListEntryCountry = {
  name: string | null
  isoCode: IsoCode
}

export type TrustedContactListEntry = {
  id: string
  workerId: string
  name: string
  initials: string
  title: string
  avatar: string
  avatarPath: string
  currency: string
  dayRate: string
  jobsCount: number
  country: TrustedContactListEntryCountry
  city: string | null
  state: string | null
  companySkills: object[]
  workerSkills: object[]
  combinedSkills: object[]
  links: string[]
  businessSetup: string | null
  isCurrentlyHired: boolean
  viewerCanContact: boolean
  viewerCanApprove: boolean
  viewerCanUpdate: boolean
  viewerCanDelete: boolean
  viewerCanReInvite: boolean
  viewerCanOfferJob: boolean
  status: ContactStatus
  statusUpdatedAt: string
  activeHiresCount: number
  pendingHiresCount: number
  company: object
  rtwVerifiedAt: string
  rtwExpiresAt: string
  verified: boolean
  hiresWithAttribution: Hire[]
  notesCount: number
  managedStatus: TrustedContactManagedStatus | null
}

export default function useTrustedContactsList(
  query: DocumentParameter<any, TrustedContactListVariables>,
  variables: Ref<TrustedContactListVariables>,
) {
  const { result, loading, refetch, error, onError } = useQuery(query, variables, {
    clientId: 'publicApiCustomFields',
  })

  const mapSkills = (skills, forCompany = false) =>
    skills.map((skill) => ({
      ...skill,
      skillType: forCompany ? AccountType.Company : AccountType.Worker,
    }))

  const getCombinedSkills = (companySkills, workerSkills) => {
    const mappedCompanySkills = mapSkills(companySkills, true)
    const mappedWorkerSkills = mapSkills(workerSkills)

    return unionBy([...mappedCompanySkills, ...mappedWorkerSkills], 'name')
  }

  const parsedResult: ComputedRef<TrustedContactListEntry[]> = computed(
    () =>
      result.value?.trustedContacts.data.map((entry) => ({
        id: entry.id,
        workerId: entry.worker.id,
        name: entry.worker.name,
        initials: entry.worker.initials,
        title: entry.worker.jobTitle,
        avatar: entry.worker.avatar,
        avatarPath: entry.worker.avatarPath,
        currency: entry.worker.currency,
        dayRate: entry.worker.dayRate,
        jobsCount: entry.worker.hires.paginatorInfo.total,
        country: entry.worker?.address?.country ?? {},
        city: entry.worker?.address?.city,
        state: entry.worker?.address?.state,
        companySkills: mapSkills(entry.skills, true),
        workerSkills: mapSkills(entry.worker.skills),
        combinedSkills: getCombinedSkills(entry.skills, entry.worker.skills),
        links: entry.links,
        businessSetup: entry.worker.primaryBusinessEntity?.name,
        viewerCanContact: entry.worker.viewerCanContact,
        viewerCanApprove: entry.viewerCanApprove,
        viewerCanUpdate: entry.viewerCanUpdate,
        viewerCanDelete: entry.viewerCanDelete,
        viewerCanReInvite: entry.viewerCanReInvite,
        viewerCanOfferJob: entry.worker.viewerCanOfferJob,
        status: entry.status,
        statusUpdatedAt: entry.statusUpdatedAt,
        isCurrentlyHired: entry.worker.isCurrentlyHired,
        acceptedHiresCount: entry.worker.hires.paginatorInfo.total,
        activeHiresCount: entry.worker.activeHires.paginatorInfo.total,
        pendingHiresCount: entry.worker.pendingHires.paginatorInfo.total,
        company: entry.company.name,
        rtwVerifiedAt: useDate(entry.worker.rtwVerifiedAt).date.value,
        rtwExpiresAt: useDate(entry.worker.rtwExpiresAt).date.value,
        verified:
          entry.worker.rtwExpiresAt !== null
            ? new Date().getTime() - new Date(entry.worker.rtwExpiresAt).getTime() < 0
            : false,
        hiresWithAttribution: entry.worker.hiresWithAttribution,
        notesCount: entry.notes.paginatorInfo.total,
        managedStatus: entry.managedStatus,
      })) ?? [],
  )

  return {
    trustedContacts: parsedResult,
    rawResult: result,
    loading,
    refetch,
    error,
    onError,
  }
}
